import { memo, useMemo } from 'react'
import Slider, { Settings } from 'react-slick'
import DPFCarouselImg1 from '../assets/landing/DPF-carousel-img-1.png'
import DPFCarouselImg2 from '../assets/landing/DPF-carousel-img-2.png'
import DPFCarouselImg3 from '../assets/landing/DPF-carousel-img-3.png'
import DPFPresentImg1 from '../assets/landing/DPF-present-img-1.png'

interface UsageScene {
  type: 'carousel' | 'single'
  images: string[]
  title: string
  description: string | string[]
}

const SliderComponent = Slider as unknown as React.ComponentClass<Settings>

function CarouselImages({ images }: { images: string[] }) {
  const carouselSettings: Settings = useMemo(
    () => ({
      dots: true,
      infinite: true,
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay: true,
      speed: 500,
      autoplaySpeed: 3000,
      cssEase: 'ease',
      arrows: false,
      swipeToSlide: true,
      appendDots: (dots: JSX.Element) => (
        <div>
          <ul className='flex justify-center gap-2 m-0 p-0 list-none'>{dots}</ul>
        </div>
      ),
      customPaging: () => (
        <div className='w-2 h-2 rounded-full bg-gray-300 transition-all duration-200' />
      ),
    }),
    [],
  )

  return (
    <div className='relative rounded-lg'>
      <style>{`
        .slick-slider {
          position: relative;
        }
        .slick-dots {
          position: absolute !important;
          bottom: 14px !important;
          left: 0 !important;
          right: 0 !important;
          z-index: 10 !important;
        }
      `}</style>
      <SliderComponent {...carouselSettings}>
        {images.map((image, idx) => (
          <div key={idx}>
            <img
              src={image}
              alt=''
              className='w-full rounded-lg'
              loading={idx === 0 ? 'eager' : 'lazy'}
            />
          </div>
        ))}
      </SliderComponent>
    </div>
  )
}

const SceneImage = memo(function SceneImage({ src, alt }: { src: string; alt: string }) {
  return <img src={src} alt={alt} className='w-full rounded-lg' loading='lazy' />
})

const SceneContent = memo(function SceneContent({
  title,
  description,
}: {
  title: string
  description: string | string[]
}) {
  const descriptionArray = Array.isArray(description) ? description : [description]

  return (
    <div className='space-y-4 text-center'>
      <h3 className='text-xl font-semibold text-gray-12 leading-[140%] tracking-0'>{title}</h3>
      <div className='text-base font-normal text-gray-12 leading-[160%] tracking-0 text-left'>
        {descriptionArray.map((paragraph, idx) => (
          <p key={idx} className={idx > 0 ? 'mt-1' : ''}>
            {paragraph}
          </p>
        ))}
      </div>
    </div>
  )
})

export default function UsageScenesSection() {
  const scenes = useMemo<UsageScene[]>(
    () => [
      {
        type: 'carousel',
        images: [DPFCarouselImg1, DPFCarouselImg2, DPFCarouselImg3],
        title: 'ご自宅のリビングに',
        description:
          'リビングや寝室など、どんな空間にも自然に溶け込むデザインです。場所を選ばず自由に飾ることができます。家族だんらんの時間に、みてねの思い出を楽しみませんか？',
      },
      {
        type: 'single',
        images: [DPFPresentImg1],
        title: 'プレゼントにもおすすめ',
        description: [
          '離れて暮らす家族への贈り物にもぴったり。',
          'ご自身のみてねアカウントでセットアップして贈ることも、相手が自分のアカウントでセットアップすることもできます。みてねに追加された新しい写真や動画も、自動で表示されます。',
          '複数アルバムの写真や動画も、1台でまとめて再生できます。',
          '※ご利用にはWi-Fiが必要です',
        ],
      },
    ],
    [],
  )

  return (
    <section className='px-4 py-10 space-y-8 bg-[#F5F5F5]'>
      <h2 className='text-2xl font-semibold text-gray-12 text-center'>ご利用シーン</h2>

      {scenes.map((scene, idx) => (
        <div key={idx} className='space-y-4'>
          {scene.type === 'carousel' ? (
            <CarouselImages images={scene.images} />
          ) : (
            <SceneImage src={scene.images[0]} alt={scene.title} />
          )}
          <SceneContent title={scene.title} description={scene.description} />
        </div>
      ))}
    </section>
  )
}
